import { createFileRoute, Link } from "@tanstack/react-router";
import { useOrganization } from "@/lib/auth";
import { Card, FeatureGate, LoadingState } from "@/design-system";
import { Lock } from "lucide-react";
import { PageChrome } from "@/components/chrome/PageChrome";
import { AuditLogPanel } from "@/components/settings/auditLog/AuditLogPanel";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useEntitlements } from "@/hooks/useBilling";

export const Route = createFileRoute("/settings/audit")({
  component: AuditPage,
});

function AuditPage() {
  const { organization } = useOrganization();
  const capabilities = useCapabilities();
  const entitlements = useEntitlements();

  if (!capabilities.loaded || entitlements.isPending) {
    return <LoadingState />;
  }

  if (!capabilities.canViewAuditLog) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 p-8">
        <Card className="max-w-md w-full p-8 flex flex-col items-center gap-4 text-center">
          <Lock size={28} className="text-fg-3" strokeWidth={1.5} />
          <h1 className="text-[20px] font-medium text-fg-1">
            Not available for your role
          </h1>
          <p className="text-[14px] text-fg-2 leading-relaxed">
            Audit log is only available to org owners.
          </p>
          <Link
            to="/settings"
            className="text-small text-accent hover:underline"
          >
            ← Back to settings
          </Link>
        </Card>
      </div>
    );
  }

  const orgName = organization?.name ?? "";
  const auditLogsEnabled = entitlements.data?.features.auditLogs ?? false;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <PageChrome
        crumbs={[
          { label: orgName || "Org", to: "/dashboard" },
          { label: "Settings" },
          { label: "Audit log" },
        ]}
        title="Audit log"
        helper="A tamper-evident record of every action taken in this workspace."
      />
      <div className="flex-1 overflow-auto px-4 sm:px-7 py-6">
        <div className="flex flex-col gap-5">
          <FeatureGate
            enabled={auditLogsEnabled}
            featureName="Audit log"
            requiredTier="Team"
            panelTitle="Audit log"
          >
            <AuditLogPanel />
          </FeatureGate>
        </div>
      </div>
    </div>
  );
}
